import { forwardRef, useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export type CurrencyInputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type" | "inputMode"> & {
  /** number = concrete value (including legitimate zero); null = empty */
  value: number | null;
  onChange: (value: number | null) => void;
  /** Currency code passed to Intl.NumberFormat. Default CAD (en-CA). */
  currency?: string;
  locale?: string;
  /** Format with currency symbol on blur (true) or plain number (false). Default true. */
  formatOnBlur?: boolean;
  allowNegative?: boolean;
  /** Max fraction digits. Default 2. */
  precision?: number;
  min?: number;
  max?: number;
  invalidMessage?: string;
  onValidityChange?: (state: "empty" | "valid" | "invalid") => void;
};

/** Parse user text safely. Returns:
 *  - { kind: "empty" } when the field is genuinely blank
 *  - { kind: "invalid" } when the input can't be interpreted as a number
 *  - { kind: "value", value } otherwise (may be 0)
 */
export function parseCurrencyInput(
  raw: string,
  opts: { allowNegative?: boolean; precision?: number } = {},
): { kind: "empty" } | { kind: "invalid" } | { kind: "value"; value: number } {
  if (raw == null) return { kind: "empty" };
  // Strip currency, whitespace (incl NBSP U+00A0, thin space U+2009, narrow NBSP U+202F), commas
  const cleaned = raw
    .replace(/[$£€¥₹]/g, "")
    .replace(/[\u00A0\u2009\u202F\s]/g, "")
    .replace(/,/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === "." || cleaned === "-.") {
    return cleaned === "" ? { kind: "empty" } : { kind: "invalid" };
  }
  // Reject anything with more than one decimal point or invalid chars
  if (!/^-?\d*(?:\.\d*)?$/.test(cleaned)) return { kind: "invalid" };
  if (!opts.allowNegative && cleaned.startsWith("-")) return { kind: "invalid" };
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return { kind: "invalid" };
  const p = opts.precision ?? 2;
  const rounded = Math.round(n * 10 ** p) / 10 ** p;
  if (!Number.isFinite(rounded)) return { kind: "invalid" };
  return { kind: "value", value: rounded };
}

export function formatCurrencyForBlur(
  value: number, locale: string, currency: string, precision: number, withSymbol: boolean,
): string {
  if (!Number.isFinite(value)) return "";
  if (withSymbol) {
    return new Intl.NumberFormat(locale, {
      style: "currency", currency,
      minimumFractionDigits: 0, maximumFractionDigits: precision,
    }).format(value);
  }
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0, maximumFractionDigits: precision,
  }).format(value);
}

export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(function CurrencyInput({
  value, onChange, currency = "CAD", locale = "en-CA", formatOnBlur = true,
  allowNegative = false, precision = 2, min, max, invalidMessage = "Enter a valid amount",
  onValidityChange, className, id: idProp, ...rest
}, ref) {
  const reactId = useId();
  const errId = `${idProp ?? reactId}-err`;

  // Editing text separate from the numeric value: field can be truly empty
  // (editingText === "") independent of value === 0.
  const [editingText, setEditingText] = useState<string>(() =>
    value == null ? "" : formatCurrencyForBlur(value, locale, currency, precision, formatOnBlur),
  );
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const lastCommittedValue = useRef<number | null>(value);

  // Reflect external value changes only when not focused; while focused, the
  // user's in-progress text is authoritative (prevents "leading zero" bug).
  useEffect(() => {
    if (focused) return;
    if (value !== lastCommittedValue.current) {
      lastCommittedValue.current = value;
      setEditingText(value == null ? "" : formatCurrencyForBlur(value, locale, currency, precision, formatOnBlur));
      setInvalid(false);
    }
  }, [value, focused, locale, currency, precision, formatOnBlur]);

  const commit = (parsed: ReturnType<typeof parseCurrencyInput>) => {
    if (parsed.kind === "empty") {
      setInvalid(false);
      onValidityChange?.("empty");
      if (lastCommittedValue.current !== null) {
        lastCommittedValue.current = null;
        onChange(null);
      }
      return;
    }
    if (parsed.kind === "invalid") {
      setInvalid(true);
      onValidityChange?.("invalid");
      // Do NOT coerce invalid text to 0
      return;
    }
    let v = parsed.value;
    if (typeof min === "number" && v < min) v = min;
    if (typeof max === "number" && v > max) v = max;
    if (!Number.isFinite(v)) {
      setInvalid(true); onValidityChange?.("invalid"); return;
    }
    setInvalid(false);
    onValidityChange?.("valid");
    if (v !== lastCommittedValue.current) {
      lastCommittedValue.current = v;
      onChange(v);
    }
  };

  return (
    <>
      <input
        {...rest}
        ref={ref}
        id={idProp}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={editingText}
        aria-invalid={invalid || undefined}
        aria-errormessage={invalid ? errId : undefined}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          invalid && "border-destructive focus-visible:ring-destructive",
          className,
        )}
        onFocus={(e) => {
          setFocused(true);
          // On focus, replace formatted text with raw digits so typing feels natural
          if (value != null) {
            const raw = String(value);
            setEditingText(raw);
            const el = e.currentTarget;
            requestAnimationFrame(() => { try { el?.select?.(); } catch { /* unmounted */ } });
          } else {
            setEditingText("");
          }
          rest.onFocus?.(e);
        }}
        onChange={(e) => {
          const text = e.target.value;
          setEditingText(text);
          // Parse in-flight for real-time validity & to keep upstream in sync
          const parsed = parseCurrencyInput(text, { allowNegative, precision });
          commit(parsed);
        }}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData("text");
          if (pasted) {
            e.preventDefault();
            const parsed = parseCurrencyInput(pasted, { allowNegative, precision });
            if (parsed.kind === "value") {
              setEditingText(String(parsed.value));
              commit(parsed);
            } else if (parsed.kind === "empty") {
              setEditingText(""); commit(parsed);
            } else {
              setEditingText(pasted); commit(parsed);
            }
          }
          rest.onPaste?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          const parsed = parseCurrencyInput(editingText, { allowNegative, precision });
          commit(parsed);
          if (parsed.kind === "value") {
            setEditingText(formatCurrencyForBlur(parsed.value, locale, currency, precision, formatOnBlur));
          } else if (parsed.kind === "empty") {
            setEditingText("");
          }
          // parsed.kind === "invalid" keeps the raw text so the user can fix it
          rest.onBlur?.(e);
        }}
      />
      {invalid && (
        <p id={errId} className="mt-1 text-xs text-destructive">{invalidMessage}</p>
      )}
    </>
  );
});